import { WindowEventMap } from '@blk/bxf-web-components';
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
} from 'react';

import { useDispatch } from 'react-redux';
import {
    activeContentPages,
    CHAT_BLK_TOGGLE_EVENT_OPEN,
    CHAT_BLK_TOKEN_STATUS_EVENT,
    CHAT_BLK_USER_STATUS_EVENT,
    OAUTH_ACCESS_TOKEN,
    UNABLE_TO_PERFORM_AUTHORIZATION_SECONDARY_MESSAGE,
    UNAUTHORIZED_SECONDARY_MESSAGE,
    URIS,
} from '../constants';
import { useCache, useChatBlkStore } from '../hooks';
import {
    ChatBlkBaseService,
    ChatBlkQueryService,
    ChatBlkTelemetryService,
} from '../services';
import { ChatBlkDispatch } from '../store/store';
import { fetchRecentPrompts, fetchRelationshipManagers } from '../store/thunk';
import { ChatBlkTokenStatusEvent, ChatBlkUserStatusEvent } from '../types';
import { getCacheValue } from '../utils';
import { useChatBlkContext } from './ChatBlkProvider';

const { LOGIN, LOADING, CHAT, LOADING_ERROR, UNVERIFIED } = activeContentPages;

export const AuthContext = createContext<{
    login: () => Promise<boolean>;
}>(undefined);

export const useAuthContext = () => useContext(AuthContext);
interface AuthProps {
    children?: React.JSX.Element;
}

/**
 * Provides auth context to its children components.
 *
 * @param {React.JSX.Element} props.children - The child components to be wrapped by the provider.
 *
 * @returns {JSX.Element} The auth context provider component.
 *
 * @remarks
 * This component uses the `useChatExchangeContext` hook to access the chat service and perform user auth.
 * It maintains a state to track whether the auth process was successful or not.
 *
 * @component
 *
 * @example
 * ```tsx
 * <AuthProvider>
 *   <YourComponent />
 * </AuthProvider>
 * ```
 *
 * @function
 * @name AuthProvider
 */
export const AuthProvider = ({ children }: AuthProps): JSX.Element => {
    const chatService = ChatBlkQueryService.getInstance();
    const { open } = useChatBlkContext();
    const { loadCache } = useCache();
    const { activeContent, setActiveContent } = useChatBlkStore();
    const dispatch = useDispatch<ChatBlkDispatch>();

    /**
     * Attempts to log in the user by requesting user login from the chat service.
     * Sets various chat service properties and updates the state based on the login status.
     * If login is successful, it sets user initials, fetches recent prompts, and loads cache data.
     *
     * @returns {Promise<boolean>} A promise that resolves to a boolean indicating whether the user is logged in.
     */
    const login = useCallback(async (): Promise<boolean> => {
        const { isLoggedIn, setUserInitials } = chatService;
        try {
            // should only load chat once the user is logged in for the first time
            if (
                isLoggedIn &&
                (activeContent === LOGIN || activeContent === LOADING)
            ) {
                setUserInitials();

                setActiveContent(CHAT);
                dispatch(fetchRecentPrompts());
                dispatch(fetchRelationshipManagers());
                await loadCache();
            }
        } catch (e) {
            console.error(e);
            ChatBlkTelemetryService.trackError({
                userHasCopilotAccess: false,
                unableToPerformAuth: !ChatBlkBaseService.getInstance(),
                message: !!ChatBlkBaseService.getInstance()
                    ? UNABLE_TO_PERFORM_AUTHORIZATION_SECONDARY_MESSAGE
                    : UNAUTHORIZED_SECONDARY_MESSAGE,
            });
        }
        return isLoggedIn;
    }, [chatService, loadCache, setActiveContent]);

    const handleTokenStatus = useCallback(
        async (event: CustomEvent<ChatBlkTokenStatusEvent>) => {
            const { status } = event.detail;
            if (status === 'generated' && !chatService.isLoggedIn) {
                await login();
            } else if (status === 'failed') {
                setActiveContent(LOADING_ERROR);
                ChatBlkTelemetryService.trackError({
                    userHasCopilotAccess: false,
                    unableToPerformAuth: true,
                    message: UNAUTHORIZED_SECONDARY_MESSAGE,
                });
            }
        },
        [chatService, login]
    );

    const handleUserStatus = useCallback(
        async (event: CustomEvent<ChatBlkUserStatusEvent>) => {
            const { status } = event.detail;
            if (status === 200 && chatService.isLoggedIn) {
                await login();
            } else if (status === 401) {
                setActiveContent(UNVERIFIED);
                ChatBlkTelemetryService.trackError({
                    userHasCopilotAccess: false,
                    unableToPerformAuth: true,
                    message: UNAUTHORIZED_SECONDARY_MESSAGE,
                });
            } else {
                setActiveContent(LOADING_ERROR);
                ChatBlkTelemetryService.trackError({
                    userHasCopilotAccess: false,
                    unableToPerformAuth: true,
                    message: UNABLE_TO_PERFORM_AUTHORIZATION_SECONDARY_MESSAGE,
                });
            }
        },
        [chatService, login]
    );

    /**
     * Handles changes to the session storage for the OAuth access token.
     * This function listens for changes in the session storage,
     * specifically for the OAUTH_ACCESS_TOKEN key.
     *
     * Alternative for @callback handleTokenStatus
     */
    const handleTokenSessionStorageChange = useCallback(
        async (event: StorageEvent) => {
            if (event.key === OAUTH_ACCESS_TOKEN) {
                // If access token is set, we assume the user is logged in
                // If access token is removed, we assume the user is logged out
                event.newValue ? await login() : setActiveContent(LOGIN);
            }
        },
        [login, setActiveContent]
    );

    // Get login page URL based on current domain
    const getLoginPageUrl = useCallback(() => {
        const domain = window.location.origin;
        return URIS.PROD_HOSTS.includes(window.location.hostname)
            ? `${domain}/us/financial-professionals/advisorcenter-login`
            : `${domain}/financial-professionals/financial-professionals/advisorcenter-login`;
    }, []);

    useEffect(() => {
        // Attach listeners for storage key and user status events
        window.addEventListener(CHAT_BLK_TOKEN_STATUS_EVENT, handleTokenStatus);
        window.addEventListener(CHAT_BLK_USER_STATUS_EVENT, handleUserStatus);
        window.addEventListener('storage', handleTokenSessionStorageChange);
        (async () => {
            if (chatService.isLoggedIn && activeContent === LOGIN) {
                if (URIS.PRE_PROD_HOSTS.includes(window.location.hostname)) {
                    setActiveContent(CHAT);
                } else {
                    getCacheValue('action') === CHAT_BLK_TOGGLE_EVENT_OPEN &&
                        open();
                    // If the user is logged in and access token is available in session storage,
                    // we can assume the user is authenticated and proceed to load the chat.
                    if (!!getCacheValue('access_token', false)) {
                        await login();
                    } else {
                        setActiveContent(LOADING);
                    }
                }
            }
            const iframeUrlEl = document.getElementById(
                'chatblk-iframe'
            ) as HTMLIFrameElement;
            const iframeUrl = iframeUrlEl?.src || getLoginPageUrl();
            const testIframeUrl = async () => {
                try {
                    const response = await fetch(iframeUrl, {
                        method: 'GET',
                        credentials: 'include', // Include cookies for authentication
                        headers: {
                            Accept: 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
                        },
                    });
                    console.log('🔍 Response Status:', response.status);
                    console.log('🔍 Response OK:', response.ok);
                    console.log(
                        '🔍 Response Headers:',
                        Object.fromEntries(response.headers)
                    );
                    if (response.status === 401) {
                        console.log(
                            '❌ User is NOT verified (401 Unauthorized)'
                        );

                        setActiveContent(UNVERIFIED);
                    } else if (response.status === 200) {
                        console.log('✅ User is verified (200 OK)');
                    } else {
                        console.log(`⚠️ Unexpected status: ${response.status}`);
                    }
                    return response.status;
                } catch (error) {
                    console.error('❌ Error testing iframe URL:', error);
                    return null;
                }
            };
            // Run the test
            iframeUrl &&
                !URIS.PRE_PROD_HOSTS.includes(window.location.hostname) &&
                (await testIframeUrl());
        })();

        // Cleanup function to remove the event listener
        return () => {
            window.removeEventListener(
                CHAT_BLK_TOKEN_STATUS_EVENT,
                handleTokenStatus
            );
            window.removeEventListener(
                CHAT_BLK_USER_STATUS_EVENT,
                handleUserStatus
            );
            window.removeEventListener(
                WindowEventMap.storage,
                handleTokenSessionStorageChange
            );
        };
    }, []);

    const contextValue = useMemo(() => ({ login }), [login]);

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};

AuthProvider.displayName = 'AuthProvider';
